import * as THREE from 'three';
import { RectAreaLightNode, WebGPURenderer } from 'three/webgpu';

type Renderer = InstanceType<typeof WebGPURenderer>;

export type ReferenceLightingQuality = 'normal' | 'large' | 'veryLarge';

export interface ReferenceLightingProfile {
  readonly shadowMapSize: number;
  readonly shadowFilter: 'pcf-soft' | 'pcf' | 'basic';
  readonly maxShadowUpdatesPerSecond: number;
  readonly minShadowUpdatesPerSecond: number;
  readonly frustumPadding: number;
  readonly areaLightIntensity: number;
  readonly shadowLightIntensity: number;
  readonly hemisphereIntensity: number;
}

/**
 * Keep every presentation tier at a 4K shadow depth target. Dense scenes need
 * the extra spatial precision most because their fitted frustum covers the
 * whole coliseum; dropping them to 2K made contact shadows visibly merge and
 * stair-step even though updates remained pose-synchronous.
 */
export const REFERENCE_LIGHTING_PROFILES: Readonly<
  Record<ReferenceLightingQuality, ReferenceLightingProfile>
> = Object.freeze({
  normal: Object.freeze({
    shadowMapSize: 4096,
    // Three's WebGPU PCF kernel performs five comparisons per shaded pixel;
    // PCFSoft expands to sixteen, so the regular PCF path keeps the higher map
    // resolution affordable without returning to a nearest hard shadow.
    shadowFilter: 'pcf',
    maxShadowUpdatesPerSecond: 60,
    minShadowUpdatesPerSecond: 60,
    frustumPadding: 0.08,
    areaLightIntensity: 6.5,
    shadowLightIntensity: 2.8,
    hemisphereIntensity: 1.35,
  }),
  large: Object.freeze({
    shadowMapSize: 4096,
    shadowFilter: 'pcf',
    maxShadowUpdatesPerSecond: 60,
    minShadowUpdatesPerSecond: 60,
    frustumPadding: 0.07,
    areaLightIntensity: 6,
    shadowLightIntensity: 2.65,
    hemisphereIntensity: 1.4,
  }),
  veryLarge: Object.freeze({
    shadowMapSize: 4096,
    // Dense 50K/64K scenes retain PCF and pose-synchronous updates; only the
    // depth target's spatial precision changes.
    shadowFilter: 'pcf',
    maxShadowUpdatesPerSecond: 60,
    minShadowUpdatesPerSecond: 60,
    frustumPadding: 0.06,
    areaLightIntensity: 5.5,
    shadowLightIntensity: 2.5,
    hemisphereIntensity: 1.45,
  }),
});

export interface ReferenceLightingOptions {
  renderer: Renderer;
  scene: THREE.Scene;
  quality?: ReferenceLightingQuality;
  /** Bounds of the shadow casters/receivers, excluding an oversized ground plane. */
  bounds?: THREE.Box3;
  enabled?: boolean;
  shadowsEnabled?: boolean;
  areaLightEnabled?: boolean;
  environmentFillEnabled?: boolean;
  environmentIntensity?: number;
  areaLightIntensity?: number;
  shadowLightIntensity?: number;
  hemisphereIntensity?: number;
  shadowFrustumPadding?: number;
}

export interface ReferenceLightingConfiguration {
  quality?: ReferenceLightingQuality;
  bounds?: THREE.Box3;
  enabled?: boolean;
  shadowsEnabled?: boolean;
  areaLightEnabled?: boolean;
  environmentFillEnabled?: boolean;
  environmentIntensity?: number;
  areaLightIntensity?: number;
  shadowLightIntensity?: number;
  hemisphereIntensity?: number;
  shadowFrustumPadding?: number;
}

export interface ShadowScheduleFrame {
  /** @deprecated Kept for source compatibility; scheduling is pose-driven. */
  nowMs?: number;
  /** @deprecated Kept for source compatibility; scheduling is pose-driven. */
  fps?: number;
  /** Set when caster poses changed since the last shadow refresh. */
  sceneDirty?: boolean;
  /** Bypass the cadence once, for resets/camera cuts/quality changes. */
  force?: boolean;
}

export interface ReferenceLightingController {
  readonly group: THREE.Group;
  readonly areaLight: THREE.RectAreaLight;
  readonly shadowLight: THREE.DirectionalLight;
  readonly hemisphereLight: THREE.HemisphereLight;
  readonly target: THREE.Object3D;
  readonly profile: ReferenceLightingProfile;
  readonly quality: ReferenceLightingQuality;
  readonly enabled: boolean;
  readonly shadowsEnabled: boolean;
  configure(options: ReferenceLightingConfiguration): void;
  setQuality(quality: ReferenceLightingQuality): void;
  setEnabled(enabled: boolean): void;
  setShadowsEnabled(enabled: boolean): void;
  fitShadowFrustum(bounds: THREE.Box3, padding?: number): void;
  invalidateShadow(): void;
  updateShadowSchedule(frame?: ShadowScheduleFrame): boolean;
  dispose(): void;
}

let rectAreaLtcInitialized = false;
let rectAreaLtcInitialization: Promise<void> | null = null;

/**
 * Load the sizeable LTC lookup tables only for tiers that actually render the
 * rectangular key. 50K/64K deliberately omit the two LTC receiver samples and
 * should not pay to parse or allocate those tables during startup.
 */
export function prepareReferenceAreaLightLtc(): Promise<void> {
  if (rectAreaLtcInitialized) return Promise.resolve();
  if (!rectAreaLtcInitialization) {
    rectAreaLtcInitialization = import('three/addons/lights/RectAreaLightTexturesLib.js')
      .then(({ RectAreaLightTexturesLib }) => {
        RectAreaLightNode.setLTC(RectAreaLightTexturesLib.init());
        rectAreaLtcInitialized = true;
      })
      .finally(() => {
        rectAreaLtcInitialization = null;
      });
  }
  return rectAreaLtcInitialization;
}

const SHADOW_DIRECTION = new THREE.Vector3(-0.12, -1, -0.08).normalize();
const DEFAULT_BOUNDS = new THREE.Box3(
  new THREE.Vector3(-8, 0, -8),
  new THREE.Vector3(8, 12, 8),
);

class ReferenceLighting implements ReferenceLightingController {
  readonly group = new THREE.Group();
  readonly areaLight = new THREE.RectAreaLight(0xfff5e0, 5.5, 8, 3.4);
  readonly shadowLight = new THREE.DirectionalLight(0xfff2db, 1.8);
  readonly hemisphereLight = new THREE.HemisphereLight(0xc7dcff, 0x425160, 0.42);
  readonly target = new THREE.Object3D();

  private readonly renderer: Renderer;
  private readonly scene: THREE.Scene;
  private readonly previousShadowEnabled: boolean;
  private readonly previousShadowType: THREE.ShadowMapType;
  private readonly previousEnvironmentIntensity: number;
  private currentQuality: ReferenceLightingQuality;
  private currentProfile: ReferenceLightingProfile;
  private currentBounds = DEFAULT_BOUNDS.clone();
  private currentPadding: number;
  private environmentIntensity: number;
  private areaLightEnabled: boolean;
  private environmentFillEnabled: boolean;
  private currentEnabled: boolean;
  private currentShadowsEnabled: boolean;
  private shadowDirty = true;
  private disposed = false;

  constructor(options: ReferenceLightingOptions) {
    this.renderer = options.renderer;
    this.scene = options.scene;
    this.previousShadowEnabled = options.renderer.shadowMap.enabled;
    this.previousShadowType = options.renderer.shadowMap.type;
    this.previousEnvironmentIntensity = options.scene.environmentIntensity;
    this.currentQuality = options.quality ?? 'normal';
    this.currentProfile = REFERENCE_LIGHTING_PROFILES[this.currentQuality];
    this.currentPadding = options.shadowFrustumPadding ?? this.currentProfile.frustumPadding;
    this.environmentIntensity = options.environmentIntensity ?? 1;
    this.areaLightEnabled = options.areaLightEnabled ?? true;
    if (this.areaLightEnabled && !rectAreaLtcInitialized) {
      throw new Error(
        'RectAreaLight LTC is not initialized; await prepareReferenceAreaLightLtc() first.',
      );
    }
    this.environmentFillEnabled = options.environmentFillEnabled ?? true;
    this.currentEnabled = options.enabled ?? true;
    this.currentShadowsEnabled = options.shadowsEnabled ?? true;

    this.group.name = 'PMavbd reference lighting';
    this.areaLight.name = 'Reference PBR rectangular key light';
    this.shadowLight.name = 'Reference scheduled directional shadow light';
    this.hemisphereLight.name = 'Reference environment fill';
    this.target.name = 'Reference shadow target';

    this.shadowLight.castShadow = true;
    this.shadowLight.target = this.target;
    this.shadowLight.shadow.autoUpdate = false;
    this.shadowLight.shadow.bias = -0.00004;
    this.shadowLight.shadow.normalBias = 0.006;
    this.shadowLight.shadow.camera.near = 0.05;
    this.shadowLight.shadow.camera.far = 100;
    this.shadowLight.shadow.mapSize.setScalar(this.currentProfile.shadowMapSize);

    this.group.add(this.areaLight, this.shadowLight, this.hemisphereLight, this.target);
    this.scene.add(this.group);

    this.applyShadowFilter();
    if (options.bounds && !options.bounds.isEmpty()) this.currentBounds.copy(options.bounds);
    this.fitShadowFrustum(this.currentBounds, this.currentPadding);

    this.areaLight.intensity = options.areaLightIntensity ?? this.currentProfile.areaLightIntensity;
    this.shadowLight.intensity = options.shadowLightIntensity ?? this.currentProfile.shadowLightIntensity;
    this.hemisphereLight.intensity = options.hemisphereIntensity
      ?? this.currentProfile.hemisphereIntensity;
    this.applyEnabledState();
    if (this.currentEnabled && this.currentShadowsEnabled) {
      this.shadowLight.shadow.needsUpdate = true;
    }
  }

  get profile(): ReferenceLightingProfile {
    return this.currentProfile;
  }

  get quality(): ReferenceLightingQuality {
    return this.currentQuality;
  }

  get enabled(): boolean {
    return this.currentEnabled;
  }

  get shadowsEnabled(): boolean {
    return this.currentShadowsEnabled;
  }

  configure(options: ReferenceLightingConfiguration): void {
    this.assertAlive();
    const wasEnabled = this.currentEnabled;
    const hadShadows = this.currentShadowsEnabled;
    if (options.quality !== undefined) this.setQuality(options.quality);
    if (options.shadowFrustumPadding !== undefined) {
      this.currentPadding = Math.max(0, options.shadowFrustumPadding);
    }
    if (options.bounds !== undefined) this.fitShadowFrustum(options.bounds, this.currentPadding);
    else if (options.shadowFrustumPadding !== undefined) {
      this.fitShadowFrustum(this.currentBounds, this.currentPadding);
    }
    if (options.areaLightIntensity !== undefined) {
      this.areaLight.intensity = Math.max(0, options.areaLightIntensity);
    }
    if (options.shadowLightIntensity !== undefined) {
      this.shadowLight.intensity = Math.max(0, options.shadowLightIntensity);
    }
    if (options.hemisphereIntensity !== undefined) {
      this.hemisphereLight.intensity = Math.max(0, options.hemisphereIntensity);
    }
    if (options.environmentIntensity !== undefined) {
      this.environmentIntensity = Math.max(0, options.environmentIntensity);
    }
    if (options.areaLightEnabled !== undefined) {
      if (options.areaLightEnabled && !rectAreaLtcInitialized) {
        throw new Error(
          'RectAreaLight LTC is not initialized; await prepareReferenceAreaLightLtc() first.',
        );
      }
      this.areaLightEnabled = options.areaLightEnabled;
    }
    if (options.environmentFillEnabled !== undefined) {
      this.environmentFillEnabled = options.environmentFillEnabled;
    }
    if (options.shadowsEnabled !== undefined) {
      this.currentShadowsEnabled = options.shadowsEnabled;
    }
    if (options.enabled !== undefined) this.currentEnabled = options.enabled;
    this.applyEnabledState();
    if ((!wasEnabled && this.currentEnabled) || (!hadShadows && this.currentShadowsEnabled)) {
      this.invalidateShadow();
    }
  }

  setQuality(quality: ReferenceLightingQuality): void {
    this.assertAlive();
    if (quality === this.currentQuality) return;
    this.currentQuality = quality;
    this.currentProfile = REFERENCE_LIGHTING_PROFILES[quality];
    this.currentPadding = this.currentProfile.frustumPadding;

    const size = this.currentProfile.shadowMapSize;
    this.shadowLight.shadow.mapSize.setScalar(size);
    // WebGPU's ShadowNode keeps the render target. Resizing that target avoids
    // rebuilding the lighting material when a runtime quality tier changes.
    this.shadowLight.shadow.map?.setSize(size, size);
    this.areaLight.intensity = this.currentProfile.areaLightIntensity;
    this.shadowLight.intensity = this.currentProfile.shadowLightIntensity;
    this.hemisphereLight.intensity = this.currentProfile.hemisphereIntensity;
    this.applyShadowFilter();
    this.fitShadowFrustum(this.currentBounds, this.currentPadding);
    this.invalidateShadow();
  }

  setEnabled(enabled: boolean): void {
    this.assertAlive();
    const wasEnabled = this.currentEnabled;
    this.currentEnabled = enabled;
    this.applyEnabledState();
    if (enabled && !wasEnabled) this.invalidateShadow();
  }

  setShadowsEnabled(enabled: boolean): void {
    this.assertAlive();
    this.currentShadowsEnabled = enabled;
    this.applyEnabledState();
    if (enabled) this.invalidateShadow();
  }

  fitShadowFrustum(bounds: THREE.Box3, padding = this.currentPadding): void {
    this.assertAlive();
    if (bounds.isEmpty()) return;

    this.currentBounds.copy(bounds);
    this.currentPadding = Math.max(0, padding);

    const center = bounds.getCenter(new THREE.Vector3());
    const size = bounds.getSize(new THREE.Vector3());
    const radius = Math.max(size.length() * 0.5, 0.5);
    const floorSize = Math.max(8, Math.max(size.x, size.z) * 1.45);

    this.target.position.copy(center);
    this.shadowLight.position.copy(center).addScaledVector(SHADOW_DIRECTION, -(radius * 2.2 + 1));
    this.areaLight.position.set(
      center.x - radius * 0.18,
      bounds.max.y + radius * 0.55,
      center.z - radius * 0.12,
    );
    this.areaLight.width = floorSize * 0.52;
    this.areaLight.height = floorSize * 0.22;
    this.areaLight.lookAt(center);

    this.group.updateMatrixWorld(true);
    const lightWorld = this.shadowLight.getWorldPosition(new THREE.Vector3());
    const targetWorld = this.target.getWorldPosition(new THREE.Vector3());
    const lightMatrixWorld = new THREE.Matrix4().lookAt(lightWorld, targetWorld, this.shadowLight.up);
    lightMatrixWorld.setPosition(lightWorld);
    const viewMatrix = lightMatrixWorld.invert();

    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let minZ = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;
    let maxZ = Number.NEGATIVE_INFINITY;
    const corner = new THREE.Vector3();
    for (let i = 0; i < 8; i++) {
      corner.set(
        (i & 1) === 0 ? bounds.min.x : bounds.max.x,
        (i & 2) === 0 ? bounds.min.y : bounds.max.y,
        (i & 4) === 0 ? bounds.min.z : bounds.max.z,
      ).applyMatrix4(viewMatrix);
      minX = Math.min(minX, corner.x);
      minY = Math.min(minY, corner.y);
      minZ = Math.min(minZ, corner.z);
      maxX = Math.max(maxX, corner.x);
      maxY = Math.max(maxY, corner.y);
      maxZ = Math.max(maxZ, corner.z);
    }

    const extentX = Math.max(maxX - minX, 0.1);
    const extentY = Math.max(maxY - minY, 0.1);
    const depthExtent = Math.max(maxZ - minZ, 0.1);
    const padX = extentX * this.currentPadding + 0.02;
    const padY = extentY * this.currentPadding + 0.02;
    const padZ = depthExtent * Math.max(this.currentPadding, 0.04) + 0.05;
    const camera = this.shadowLight.shadow.camera;
    camera.left = minX - padX;
    camera.right = maxX + padX;
    camera.bottom = minY - padY;
    camera.top = maxY + padY;
    camera.near = Math.max(0.01, -maxZ - padZ);
    camera.far = Math.max(camera.near + 0.1, -minZ + padZ);
    camera.updateProjectionMatrix();

    const worldTexel = Math.max(extentX, extentY) / this.currentProfile.shadowMapSize;
    // A texel-relative offset avoids acne after a frustum resize, while the
    // cap prevents visible contact detachment in very large scenes.
    this.shadowLight.shadow.normalBias = Math.max(0.0015, Math.min(0.006, worldTexel * 0.10));
    this.invalidateShadow();
  }

  invalidateShadow(): void {
    this.assertAlive();
    this.shadowDirty = true;
  }

  updateShadowSchedule(frame: ShadowScheduleFrame = {}): boolean {
    this.assertAlive();
    if (frame.sceneDirty) this.shadowDirty = true;
    if (!this.currentEnabled || !this.currentShadowsEnabled || !this.renderer.shadowMap.enabled) {
      return false;
    }

    // The caller marks dirty only when the GPU-visible pose revision changes.
    // Update that exact pose before it is presented; wall-clock throttling made
    // shadows trail moving bodies by as many as twelve simulation steps.
    if (!frame.force && !this.shadowDirty) return false;

    this.shadowLight.shadow.needsUpdate = true;
    this.shadowDirty = false;
    return true;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    this.scene.remove(this.group);
    this.shadowLight.shadow.map?.dispose();
    this.shadowLight.shadow.map = null;
    this.shadowLight.shadow.mapPass?.dispose();
    this.shadowLight.shadow.mapPass = null;
    this.renderer.shadowMap.enabled = this.previousShadowEnabled;
    this.renderer.shadowMap.type = this.previousShadowType;
    this.scene.environmentIntensity = this.previousEnvironmentIntensity;
    this.group.clear();
  }

  private applyEnabledState(): void {
    this.group.visible = this.currentEnabled;
    this.areaLight.visible = this.currentEnabled && this.areaLightEnabled;
    this.hemisphereLight.visible = this.currentEnabled && this.environmentFillEnabled;
    this.shadowLight.visible = this.currentEnabled;
    this.shadowLight.castShadow = this.currentEnabled && this.currentShadowsEnabled;
    this.renderer.shadowMap.enabled = this.currentEnabled && this.currentShadowsEnabled;
    this.scene.environmentIntensity = this.currentEnabled && this.environmentFillEnabled
      ? this.environmentIntensity
      : 0;
  }

  private applyShadowFilter(): void {
    this.renderer.shadowMap.type = this.currentProfile.shadowFilter === 'pcf-soft'
      ? THREE.PCFSoftShadowMap
      : this.currentProfile.shadowFilter === 'pcf'
        ? THREE.PCFShadowMap
        : THREE.BasicShadowMap;
  }

  private assertAlive(): void {
    if (this.disposed) throw new Error('ReferenceLighting has been disposed.');
  }
}

export function createReferenceLighting(
  options: ReferenceLightingOptions,
): ReferenceLightingController {
  return new ReferenceLighting(options);
}
